import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AddressParts, availableZipCodes, IOlavEntry, ISolrDocList, ISolrGroup, ISolrResponse } from '@mwe/models';
import { catchError, filter, map, startWith, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AddressSearchLogic {
  private readonly SEARCH_URL = '/api/solr/olav/search';
  private readonly SPECIAL_CHARS_REGEX = /[()]/g;
  private readonly WHITESPACE_REGEX = /\s/g;

  private http = inject(HttpClient);
  private translationService = inject(TranslateService);

  search(value: AddressParts): Observable<ISolrDocList[]> {
    return of(value).pipe(
      // Early validation to prevent unnecessary HTTP calls
      filter(addressParts => this.isValidAddressParts(addressParts)),

      // Transform to HTTP params
      map(addressParts => this.generateAddressParams(addressParts)),

      // Make HTTP request with error handling
      switchMap(params =>
        this.http.get<ISolrResponse>(this.SEARCH_URL, { params }).pipe(catchError(() => of({ grouped: null } as ISolrResponse))),
      ),

      // Process the entire response chain in one transformation
      map(response => this.processCompleteResponse(response, value)),

      // Provide fallback for empty results
      startWith([]),
    );
  }

  private isValidAddressParts(value: AddressParts): boolean {
    return Boolean(value && Object.values(value).some(val => val && val.trim().length > 0));
  }

  private processCompleteResponse(response: ISolrResponse, addressParts: AddressParts): ISolrDocList[] {
    // Early return for invalid response
    if (!response?.grouped) {
      return [this.getNoResultsMessage()];
    }

    const groups = this.getFirstAvailableGroup(response.grouped);
    if (!groups?.groups?.length) {
      return [this.getNoResultsMessage()];
    }

    // Process all transformations in a single reduce operation
    const processedSuggestions = groups.groups.reduce((acc: ISolrDocList[], group) => {
      const processedGroup = this.processGroup(group);
      if (!processedGroup || this.shouldFilterOutGroup(processedGroup)) {
        return acc;
      }

      // Clean duplicated keys inline
      const cleanedGroup = this.cleanDuplicatedAnlageKeys(processedGroup);

      // Handle door splitting inline if needed
      if (addressParts.door && cleanedGroup.docs.length > 1) {
        acc.push(...this.splitDoorSuggestion(cleanedGroup));
      } else {
        acc.push(cleanedGroup);
      }

      return acc;
    }, []);

    // Return sorted results or no results message
    return processedSuggestions.length > 0 ? this.sortSuggestions(processedSuggestions) : [this.getNoResultsMessage()];
  }

  private shouldFilterOutGroup(suggestion: ISolrDocList): boolean {
    const firstDoc = suggestion.docs[0];
    return this.isIncompleteOlavKey(firstDoc.olavKey, suggestion.identNumFound);
  }

  private splitDoorSuggestion(suggestion: ISolrDocList): ISolrDocList[] {
    const [firstDoc, ...restDocs] = suggestion.docs;
    const splitSuggestions: ISolrDocList[] = [];

    // Process first document
    const updatedFirstDoc = { ...firstDoc };
    this.updateTuerNr(updatedFirstDoc);
    splitSuggestions.push({
      ...suggestion,
      docs: [updatedFirstDoc],
    });

    // Process remaining documents with unique door tracking
    const uniqueTuerNr = new Set([updatedFirstDoc.tuerNr]);

    for (const doc of restDocs) {
      const updatedDoc = { ...doc };
      this.updateTuerNr(updatedDoc);

      if (!uniqueTuerNr.has(updatedDoc.anladr1)) {
        uniqueTuerNr.add(updatedDoc.anladr1);
        splitSuggestions.push({
          ...suggestion,
          docs: [updatedDoc],
        });
      }
    }

    return splitSuggestions;
  }

  private sortSuggestions(suggestions: ISolrDocList[]): ISolrDocList[] {
    return suggestions.sort((a, b) => {
      const aDoc = a?.docs?.[0];
      const bDoc = b?.docs?.[0];

      if (!aDoc || !bDoc) return 0;
      return this.compareFields(aDoc, bDoc, ['plz', 'gemeinde', 'strasse', 'gebadr1', 'tuerNr']);
    });
  }

  private getNoResultsMessage(): ISolrDocList {
    return [this.translationService.instant('address-input.hasNoResults.title')] as unknown as ISolrDocList;
  }

  private generateAddressParams(value: AddressParts): HttpParams {
    return Object.entries(value).reduce((params, [key, val]) => {
      if (!val?.length) return params;
      return params.append(key, this.replaceSpecialChars(val));
    }, new HttpParams());
  }

  private replaceSpecialChars(value: string): string {
    return value.replace(this.SPECIAL_CHARS_REGEX, '*').replace(this.WHITESPACE_REGEX, '?');
  }

  private getFirstAvailableGroup(grouped: ISolrResponse['grouped']) {
    return grouped.gemeinde || grouped.strasse || grouped.gebadr1 || grouped.tuerNr || grouped.anladr1;
  }

  private processGroup(group: ISolrGroup['groups'][number]) {
    if (!group?.doclist?.docs?.length) return null;

    const firstDoc = group.doclist.docs[0];
    if (!this.isAnAvailableZipCode(firstDoc?.plz)) return null;

    return { ...group.doclist, groupValue: group.groupValue } as ISolrDocList;
  }

  private updateTuerNr(doc: IOlavEntry): void {
    if (doc.tuerNr !== doc.anladr1) {
      if (doc.anladr1?.trim().startsWith(doc.gebadr1)) {
        const newTuerNr = doc.anladr1.trim().replace(doc.gebadr1, '').trim().replace('/', '');
        doc.tuerNr = newTuerNr.replace('+', ' ');
      } else {
        doc.tuerNr = doc.anladr1?.trim();
      }
    }
  }

  private cleanDuplicatedAnlageKeys(suggestion: ISolrDocList): ISolrDocList {
    const seenAnlageKeys = new Set<string>();

    return {
      ...suggestion,
      docs: suggestion.docs.filter(doc => {
        if (seenAnlageKeys.has(doc.anlageKey)) {
          return false;
        }
        seenAnlageKeys.add(doc.anlageKey);
        return true;
      }),
    };
  }

  private isIncompleteOlavKey(olavKey: string, identMatches: number): boolean {
    return identMatches <= 1 && olavKey.includes('null');
  }

  private isAnAvailableZipCode(plz: string): boolean {
    return availableZipCodes.includes(plz);
  }

  private compareFields(aDoc: IOlavEntry, bDoc: IOlavEntry, fields: string[]): number {
    for (const field of fields) {
      const aValue = aDoc[field] || '';
      const bValue = bDoc[field] || '';

      if (aValue !== bValue) {
        return aValue.localeCompare(bValue, 'de', { numeric: true, sensitivity: 'accent' });
      }
    }
    return 0;
  }
}
